import { Material } from '@types'

/** Campos de zona que nunca deben sobrescribirse al aplicar un material del catálogo. */
export const ZONE_SPECIFIC_TEXTURE_KEYS = [
  'zoneName',
  'zoneDisplayName',
  'zone',
  'linkedTextureID',
  'idAcabadoUnidadModeloAcabado',
] as const

/**
 * Identidad de textura: lo que se persiste (searchID) y se muestra en el editor.
 * Pertenece a `textures[]`.
 */
export const TEXTURE_IDENTITY_KEYS = [
  'id',
  'searchID',
  'groupID',
  'name',
  'url',
  'acabado',
  'modelo',
] as const

/**
 * Parámetros de render que `handleNodeTextures` / el GLB ya usan en `textures[]`.
 */
export const TEXTURE_RENDER_KEYS = [
  'color',
  'roughness',
  'metalness',
  'envMapIntensity',
  'bumpScale',
  'opacity',
  'repeatX',
  'repeatY',
  'textureRotation',
  'mirror',
  'bumpMap',
  'displacement',
  'displacementScale',
] as const

/**
 * Metadatos del catálogo que solo deben vivir en `zones[*].material`, no en `textures[]`.
 * Si aparecen en `textures[]` es porque se copiaron por error al cambiar acabado.
 */
export const ZONE_ONLY_CATALOG_KEYS = ['description', 'descripcion', 'complemento', 'img'] as const

const TEXTURE_ENTRY_UPDATE_KEYS = [...TEXTURE_IDENTITY_KEYS, ...TEXTURE_RENDER_KEYS] as const

type TextureEntryRecord = Record<string, unknown>

export function pickTextureEntryFields(material: TextureEntryRecord): TextureEntryRecord {
  const picked: TextureEntryRecord = {}
  for (const key of TEXTURE_ENTRY_UPDATE_KEYS) {
    if (material[key] !== undefined) {
      picked[key] = material[key]
    }
  }
  return picked
}

/** Actualiza `textures[]` sin meter metadatos que pertenecen solo a `zones.material`. */
export function applyMaterialToTextureEntry<T extends TextureEntryRecord>(
  texture: T,
  material: TextureEntryRecord,
): T {
  const picked = pickTextureEntryFields(material)
  return Object.assign(texture, picked)
}

export function pickZoneStructuralFields(existing: TextureEntryRecord): TextureEntryRecord {
  const picked: TextureEntryRecord = {}
  for (const key of ZONE_SPECIFIC_TEXTURE_KEYS) {
    if (existing[key] !== undefined) picked[key] = existing[key]
  }
  return picked
}

/**
 * Sustituye `textures[]` por los campos heredados (PBR + identidad), conservando solo
 * metadatos de zona del lateral/costado. No arrastra valores del GLB anterior.
 */
export function replaceTextureEntryFromInheritedSource<T extends TextureEntryRecord>(
  texture: T,
  source: TextureEntryRecord,
): T {
  const structural: TextureEntryRecord = {
    zoneName: texture.zoneName,
    zoneDisplayName: texture.zoneDisplayName,
    zone: texture.zone,
    modelo: texture.modelo,
    linkedTextureID: texture.linkedTextureID,
    idAcabadoUnidadModeloAcabado: texture.idAcabadoUnidadModeloAcabado,
  }
  const inherited = pickTextureEntryFields(source)
  const next: TextureEntryRecord = { ...structural }
  for (const key of TEXTURE_ENTRY_UPDATE_KEYS) {
    if (inherited[key] !== undefined) next[key] = inherited[key]
  }
  return Object.assign(texture, next) as T
}

export function pickZoneCatalogFields(material: TextureEntryRecord): TextureEntryRecord {
  const picked: TextureEntryRecord = {}
  for (const key of ZONE_ONLY_CATALOG_KEYS) {
    if (material[key] !== undefined) picked[key] = material[key]
  }
  return picked
}

/**
 * Zona 3D desde material fuente (p. ej. Puerta) sin arrastrar img/PBR residual del GLB del lateral.
 */
export function replaceZoneMaterialFromInheritedSource(
  texture: TextureEntryRecord,
  existingMaterial: TextureEntryRecord,
  sourceMaterial: TextureEntryRecord,
): TextureEntryRecord {
  const structural = pickZoneStructuralFields(existingMaterial)
  const fromTexture = pickTextureEntryFields(texture)
  const catalog = pickZoneCatalogFields(sourceMaterial)
  const name =
    (fromTexture.name as string | undefined) ??
    (sourceMaterial.name as string | undefined) ??
    (texture.name as string | undefined)

  return {
    ...structural,
    ...fromTexture,
    ...catalog,
    description:
      catalog.description ??
      catalog.descripcion ??
      sourceMaterial.description ??
      sourceMaterial.descripcion ??
      name,
    descripcion:
      catalog.descripcion ??
      catalog.description ??
      sourceMaterial.descripcion ??
      sourceMaterial.description ??
      name,
    img: catalog.img ?? sourceMaterial.img,
    complemento: catalog.complemento ?? sourceMaterial.complemento,
    zoneDisplayName:
      (texture.zoneDisplayName as string | undefined) ??
      (structural.zoneDisplayName as string | undefined) ??
      (existingMaterial.zoneDisplayName as string | undefined),
    zone:
      (texture.zone as string | undefined) ??
      (texture.zoneDisplayName as string | undefined) ??
      (existingMaterial.zone as string | undefined),
  }
}

/** Zona 3D desde `textures[]` sin mezclar PBR residual del material GLB previo. */
export function replaceZoneMaterialFromTexture(
  texture: TextureEntryRecord,
  existingMaterial: TextureEntryRecord = {},
  sourceMaterial?: TextureEntryRecord,
): TextureEntryRecord {
  if (sourceMaterial) {
    return replaceZoneMaterialFromInheritedSource(texture, existingMaterial, sourceMaterial)
  }
  const structural = pickZoneStructuralFields(existingMaterial)
  const fromTexture = pickTextureEntryFields(texture)
  const catalog = pickZoneCatalogFields(texture)
  return {
    ...structural,
    ...fromTexture,
    ...catalog,
    description:
      catalog.description ??
      catalog.descripcion ??
      texture.description ??
      texture.descripcion ??
      texture.name,
    descripcion:
      catalog.descripcion ??
      catalog.description ??
      texture.descripcion ??
      texture.description ??
      texture.name,
    img: catalog.img,
    complemento: catalog.complemento,
    zoneDisplayName:
      (texture.zoneDisplayName as string | undefined) ??
      (structural.zoneDisplayName as string | undefined) ??
      (existingMaterial.zoneDisplayName as string | undefined),
    zone:
      (texture.zone as string | undefined) ??
      (texture.zoneDisplayName as string | undefined) ??
      (existingMaterial.zone as string | undefined),
  }
}

/** Textura Costado de un lateral/columna heredando por completo la Puerta del mueble. */
export function buildLateralCostadoTextureFromPuerta(
  lateralTexture: TextureEntryRecord,
  puertaTexture: TextureEntryRecord,
  puertaZoneMaterial?: TextureEntryRecord,
): TextureEntryRecord {
  const source = puertaZoneMaterial
    ? { ...puertaTexture, ...pickZoneCatalogFields(puertaZoneMaterial) }
    : puertaTexture
  return replaceTextureEntryFromInheritedSource(
    {
      ...lateralTexture,
      zoneDisplayName: lateralTexture.zoneDisplayName ?? 'Costado',
      zone: lateralTexture.zone ?? lateralTexture.zoneDisplayName ?? 'Costado',
    },
    source,
  )
}

/** Puerta/Costado/Encimera AcabadoModelo requieren MeshStandardMaterial (PBR), no Lambert. */
export function isPbrAcabadoModeloZone(
  zoneMaterial?: TextureEntryRecord,
  textureEntry?: TextureEntryRecord,
): boolean {
  const modelo = String(zoneMaterial?.modelo ?? textureEntry?.modelo ?? '')
  if (modelo === 'AcabadoModelo' || /encimera/i.test(modelo)) return true
  const display = zoneMaterial?.zoneDisplayName ?? textureEntry?.zoneDisplayName ?? textureEntry?.zone
  return (
    display === 'Puerta' ||
    display === 'Costado' ||
    display === 'Frontal' ||
    display === 'Encimera'
  )
}

/** Detecta miniaturas de catálogo de otro acabado (p. ej. S201B con img de S132M). */
export function isStaleZoneCatalogImg(textureName: string, img: unknown): boolean {
  if (!textureName || !img) return false
  const token = String(textureName).trim().split(/\s+/)[0]?.toUpperCase()
  if (!token || token.length < 3) return false
  return !String(img).toUpperCase().includes(token)
}

/** Material completo para `zones[*].material` (render 3D + catálogo). */
export function applyMaterialToZoneMaterial(
  existing: TextureEntryRecord,
  material: TextureEntryRecord,
  options?: { replace?: boolean },
): TextureEntryRecord {
  if (options?.replace) {
    return replaceZoneMaterialFromTexture(material, existing)
  }
  return { ...existing, ...material }
}

export function getAppliedTextureForZone(
  textures: TextureEntryRecord[] | undefined,
  zones: Record<string, { material?: TextureEntryRecord; name?: string }> | undefined,
  zoneName?: string,
  zoneDisplayName?: string,
): TextureEntryRecord | null {
  if (!textures?.length) return null

  const texture =
    (zoneName ? textures.find((t) => t.zoneName === zoneName) : undefined) ??
    textures.find((t) => zoneDisplayName && t.zoneDisplayName === zoneDisplayName) ??
    null

  if (!texture) return null

  const zoneKey =
    (zoneName && zones?.[zoneName] ? zoneName : null) ??
    Object.entries(zones ?? {}).find(
      ([key, zone]) =>
        key === texture.zoneName ||
        zone?.name === texture.zoneName ||
        zone?.material?.zoneDisplayName === zoneDisplayName ||
        zone?.name === zoneName,
    )?.[0]

  const zoneMaterial = zoneKey ? zones?.[zoneKey]?.material : undefined
  if (!zoneMaterial) return texture

  return {
    ...texture,
    description:
      texture.description ??
      texture.descripcion ??
      zoneMaterial.description ??
      zoneMaterial.descripcion,
    complemento: texture.complemento ?? zoneMaterial.complemento,
    img: texture.img ?? zoneMaterial.img,
  }
}

/** Laterales/costados (LA, LB, LC, CC, CB): heredan PBR de la puerta en zona Costado. */
export function isLateralOrCostadoLinkedCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedCatalogCode(item)
  return /^(LA|LB|LC|CC|CB)\d/.test(code)
}

/** Regletas (RA, RB, RC): heredan PBR de la puerta en zona Puerta. */
export function isRegletaPuertaLinkedCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  const code = getLinkedCatalogCode(item)
  return /^(RA|RB|RC)\d/.test(code)
}

export function isPuertaInheritanceLinkedCatalog(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): boolean {
  return isLateralOrCostadoLinkedCatalog(item) || isRegletaPuertaLinkedCatalog(item)
}

export type LinkedTextureModeEditor = 'Single' | 'All' | 'Selected' | 'MBJ' | 'MAL' | 'COL'

const PUERTA_INHERITANCE_PREFIXES_BY_MODE: Record<
  Extract<LinkedTextureModeEditor, 'MBJ' | 'MAL' | 'COL'>,
  readonly string[]
> = {
  COL: ['CC', 'LC', 'RC'],
  MAL: ['LA', 'RA'],
  MBJ: ['LB', 'CB', 'RB'],
}

function getLinkedCatalogCode(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): string {
  return String(item?.catalogID ?? item?.codigo ?? '')
    .trim()
    .toUpperCase()
}

/** Zona que hereda el acabado de puerta según el prefijo de catálogo. */
export function getPuertaInheritanceZoneDisplayName(
  item?: { catalogID?: string | number; codigo?: string | number } | null,
): 'Costado' | 'Puerta' | null {
  if (isRegletaPuertaLinkedCatalog(item)) return 'Puerta'
  if (isLateralOrCostadoLinkedCatalog(item)) return 'Costado'
  return null
}

/** Laterales, costados y regletas que deben heredar la puerta según el modo del editor.
 * Single no hereda: solo la zona de la unidad seleccionada.
 */
export function matchesLateralCostadoForModeEditor(
  item: { catalogID?: string | number; codigo?: string | number } | null | undefined,
  modeEditor: LinkedTextureModeEditor,
): boolean {
  if (!isPuertaInheritanceLinkedCatalog(item)) return false
  if (modeEditor === 'All' || modeEditor === 'Selected') {
    return true
  }
  if (modeEditor === 'Single') return false

  const code = getLinkedCatalogCode(item)
  return PUERTA_INHERITANCE_PREFIXES_BY_MODE[modeEditor].some((prefix) => code.startsWith(prefix))
}

export function shouldPreserveZoneBrightnessForLinkedTarget(
  target: { catalogID?: string | number; codigo?: string | number; description?: string },
  source: { description?: string },
): boolean {
  if (target.description === source.description) return false
  if (isPuertaInheritanceLinkedCatalog(target)) return false
  return true
}

export function preserveZoneBrightness(
  existing: TextureEntryRecord,
  next: TextureEntryRecord,
): TextureEntryRecord {
  return {
    ...next,
    envMapIntensity:
      (existing.envMapIntensity as number | undefined) ??
      (next.envMapIntensity as number | undefined) ??
      0,
    metalness:
      (existing.metalness as number | undefined) ?? (next.metalness as number | undefined) ?? 0,
    roughness:
      (existing.roughness as number | undefined) ?? (next.roughness as number | undefined) ?? 1,
  }
}

export function findZocaloCatalogOption(
  zocaloOptions: Array<Record<string, unknown>>,
  complementoName: string,
): Record<string, unknown> | undefined {
  const cmp = complementoName.toLowerCase().trim()
  if (!cmp) return undefined

  return zocaloOptions.find((option) => {
    const nombre = String(option.nombre ?? '')
      .toLowerCase()
      .trim()
    const descripcion = String(option.descripcion ?? '')
      .toLowerCase()
      .trim()
    const fichero = String(option.nombreFichero ?? '')
      .toLowerCase()
      .trim()
    return (
      nombre === cmp ||
      descripcion === cmp ||
      nombre.includes(cmp) ||
      descripcion.includes(cmp) ||
      fichero.includes(cmp)
    )
  })
}

type LinkedVisualTexture = {
  zoneName?: string
  searchID?: unknown
  url?: string
  color?: string
  roughness?: number
  acabado?: string
}

type LinkedVisualCarrier = {
  materialEpoch?: number
  textures?: LinkedVisualTexture[]
}

/**
 * Señal de que el acabado del ítem cambió de verdad (no solo la identidad del Map).
 * ObjectMeshENC / LinkedItem3D no pueden fiarse de la referencia de `textures[]`:
 * immer a veces muta in-place y el mesh se queda con el mapa anterior hasta un remount 2D/3D.
 */
export function bumpLinkedItemMaterialEpoch(item: LinkedVisualCarrier | null | undefined): number {
  if (!item) return 0
  const next = (item.materialEpoch ?? 0) + 1
  item.materialEpoch = next
  return next
}

export function getLinkedZoneVisualKey(texture?: LinkedVisualTexture | null): string {
  if (!texture) return ''
  return [
    texture.searchID ?? '',
    texture.url ?? '',
    texture.color ?? '',
    texture.roughness ?? '',
    texture.acabado ?? '',
  ].join('|')
}

export function getLinkedItemVisualSignature(item?: LinkedVisualCarrier | null): string {
  if (!item) return ''
  const tex = (item.textures ?? [])
    .map(
      (t) =>
        `${t.zoneName ?? ''}:${getLinkedZoneVisualKey(t)}`,
    )
    .join(';')
  return `${item.materialEpoch ?? 0}|${tex}`
}
